import {
  agencyFeePeriodPrice,
  ANNUAL_DISCOUNT_PERCENT,
  BILLING_PLAN_CATALOGUE,
  BILLING_TRIAL_DAYS,
  planPeriodPrice,
  type BillingInterval,
  type BillingPlan,
} from 'definitions/billing-plans';
import { BILLING_PLANS } from 'definitions';

/** The sessions-a-month slider's steps. The last means "more than the biggest plan counts". */
export const TRAFFIC_STEPS = [
  50_000, 100_000, 175_000, 250_000, 400_000, 600_000, 850_000, 1_100_000, 1_500_000,
] as const;
export const DEFAULT_TRAFFIC_INDEX = 3;
export const POPULAR_PLAN: BillingPlan = 'scale';

const DAY_MS = 24 * 60 * 60 * 1000;

/** The smallest plan that covers the traffic; the last step, 1.5M and up, is Unlimited. */
export function recommendedPlan(trafficIndex: number): BillingPlan {
  if (trafficIndex >= TRAFFIC_STEPS.length - 1) return 'unlimited';
  const sessions = TRAFFIC_STEPS[trafficIndex] ?? 0;
  const fits = BILLING_PLANS.find((plan) => {
    const cap = BILLING_PLAN_CATALOGUE[plan].sessionsPerMonth;
    return cap === null || cap >= sessions;
  });
  return fits ?? 'unlimited';
}

/** "250k", "1.1M", and "1.5M+" for the last step. */
export function formatTraffic(trafficIndex: number): string {
  const sessions = TRAFFIC_STEPS[trafficIndex] ?? 0;
  if (trafficIndex >= TRAFFIC_STEPS.length - 1) return '1.5M+';
  return sessions >= 1_000_000
    ? `${(sessions / 1_000_000).toFixed(1).replace('.0', '')}M`
    : `${Math.round(sessions / 1000)}k`;
}

type PriceParams = { plan: BillingPlan; interval: BillingInterval };

/** Euro cents per month as shown on a card: the annual price over twelve months. */
export function monthlyEquivalent({ plan, interval }: PriceParams): number {
  const period = planPeriodPrice({ plan, interval });
  return interval === 'year' ? Math.round(period / 12) : period;
}

export function agencyFeeMonthlyEquivalent(interval: BillingInterval): number {
  const period = agencyFeePeriodPrice(interval);
  return interval === 'year' ? Math.round(period / 12) : period;
}

/** What paying yearly saves on one plan over twelve monthly payments, in euro cents. */
export const yearlySaving = (plan: BillingPlan): number =>
  Math.round((BILLING_PLAN_CATALOGUE[plan].monthlyPrice * 12 * ANNUAL_DISCOUNT_PERCENT) / 100);

export const agencyFeeYearlySaving = (): number => agencyFeePeriodPrice('month') * 12 - agencyFeePeriodPrice('year');

/** The day the first charge happens if nobody cancels: today plus the trial. */
export const firstChargeDate = (now: Date): Date => new Date(now.getTime() + BILLING_TRIAL_DAYS * DAY_MS);

export const formatShortDate = (date: Date): string =>
  date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
